import { motion } from 'framer-motion';
import ArrowRightLight from '@/assets/arrowRightLight.svg';
import XLight from '@/assets/xLight.svg';
import CheckboxCircleBold from '@/assets/checkboxCircleBold.svg';
import Modal from '@/components/general/Modal';
import ProgressCircular from '@/components/general/ProgressCircular';
import type { AnalyticsBuilderReportData, AnalyticsBuilderResolvedTemplateType } from '@/types';

type AnalyticsBuilderGenerateModalProps = {
  open: boolean;
  template: AnalyticsBuilderResolvedTemplateType | null;
  reportData: AnalyticsBuilderReportData | null;
  onClose: () => void;
  onBack: () => void;
};

const AnalyticsBuilderGenerateModal = ({
  open,
  template,
  reportData,
  onClose,
  onBack,
}: AnalyticsBuilderGenerateModalProps) => {
  if (!template || !reportData) {
    return null;
  }

  const sectionById = new Map(reportData.sections.map((section) => [section.id, section]));
  const steps = template.areas;
  const finishedStepsCount = steps.filter((item) => sectionById.get(item.id)?.status !== 'loading').length;
  const requestInProgress = finishedStepsCount < steps.length;
  const progress = steps.length ? (finishedStepsCount / steps.length) * 100 : 100;

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableCloseOnBackdropClick
      contanerClassName="max-h-[92vh] w-full max-w-11/12 xl:max-w-3xl overflow-hidden bg-white shadow-2xl"
    >
      <>
        <div className="flex items-center justify-between border-b border-zinc-100 px-6 py-5">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="btn icon">
              <ArrowRightLight className="h-7 w-7 rotate-180" />
            </button>
            <div>
              <p className="text-xs font-semibold tracking-wide text-zinc-500 uppercase">Generating</p>
              <h2 className="text-xl font-semibold text-zinc-950">{template.displayName}</h2>
            </div>
          </div>
          <button onClick={onClose} className="btn icon">
            <XLight className="h-7 w-7" />
          </button>
        </div>
        <div className="max-h-[calc(92vh-85px)] overflow-y-auto p-8" data-lenis-prevent>
          <div className="mb-8 rounded-[6px] bg-zinc-50 p-6">
            <div className="mb-4 flex items-center gap-3">
              {requestInProgress && (
                <ProgressCircular indeterminate size={20} width={2} colorClassName="stroke-zinc-900" />
              )}
              <div>
                <h3 className="text-2xl font-semibold text-zinc-950">Building your report</h3>
                <p className="text-sm text-zinc-600">{reportData.subject}</p>
              </div>
            </div>
            <div className="h-2 overflow-hidden rounded-[6px] bg-zinc-200">
              <motion.div className="h-full bg-zinc-950" animate={{ width: `${Math.min(progress, 100)}%` }} />
            </div>
          </div>

          <div className="space-y-1 border border-zinc-100">
            {steps.map((item) => {
              const status = sectionById.get(item.id)?.status || 'loading';

              return (
                <div key={item.id} className="flex items-center justify-between rounded-[6px] p-2">
                  <span className="text-sm font-medium text-zinc-800">{item.displayName}</span>
                  {status === 'done' ? (
                    <CheckboxCircleBold className="h-5 w-5 fill-zinc-800" />
                  ) : status === 'failed' ? (
                    <span className="text-xs font-medium text-red-600">Failed</span>
                  ) : (
                    <ProgressCircular indeterminate size={20} width={2} colorClassName="stroke-zinc-800" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </>
    </Modal>
  );
};

export default AnalyticsBuilderGenerateModal;
